import { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiFilter,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiDollarSign,
  FiPackage,
  FiTrendingUp,
  FiLayers,
} from "react-icons/fi";

import AddCategoryModal from "../components/AddCategoryModal";
import AddCostPriceModal from "../components/AddCostPriceModal";

import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../services/categoryApi";

import {
  getCostPrices,
  createCostPrice,
  updateCostPrice,
  deleteCostPrice,
} from "../services/costPriceApi";

export default function CostPriceList() {
  const [categories, setCategories] = useState([]);
  const [prices, setPrices] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);

  const fetchData = async () => {
    const [catRes, priceRes] = await Promise.all([
      getCategories(),
      getCostPrices(),
    ]);

    setCategories(catRes.data);
    setPrices(priceRes.data);
  };

  useEffect(() => {
    fetchData().catch((err) => {
      console.error(err);
      alert(err.response?.data?.message || "Error loading data");
    });
  }, []);

  const stats = useMemo(() => {
    const totalProducts = prices.length;
    const activePrices = prices.filter((p) => p.status === "Active").length;
    const avgCost = prices.length
      ? prices.reduce((sum, p) => sum + Number(p.cost || 0), 0) / prices.length
      : 0;
    const suppliers = new Set(
      prices.map((p) => (p.supplier || "").trim()).filter(Boolean)
    ).size;

    return { totalProducts, activePrices, avgCost, suppliers };
  }, [prices]);

  const filteredPrices = useMemo(() => {
    const q = search.toLowerCase();

    return prices.filter((item) => {
      const matchesSearch =
        !q ||
        (item.productName || "").toLowerCase().includes(q) ||
        (item.supplier || "").toLowerCase().includes(q) ||
        (item.category?.name || "").toLowerCase().includes(q);

      const matchesCategory =
        !selectedCategory || Number(item.categoryId) === Number(selectedCategory);

      const matchesStatus = !selectedStatus || item.status === selectedStatus;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [prices, search, selectedCategory, selectedStatus]);

  const openAddCategory = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const openAddPrice = () => {
    setEditingPrice(null);
    setShowPriceModal(true);
  };

  const openEditPrice = (price) => {
    setEditingPrice(price);
    setShowPriceModal(true);
  };

  const handleSaveCategory = async (payload) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
      } else {
        await createCategory(payload);
      }
      setEditingCategory(null);
      setShowCategoryModal(false);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Error saving category");
    }
  };

  const handleSavePrice = async (payload) => {
    try {
      if (editingPrice) {
        await updateCostPrice(editingPrice.id, payload);
      } else {
        await createCostPrice(payload);
      }
      setEditingPrice(null);
      setShowPriceModal(false);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Error saving price");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await deleteCategory(id);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Error deleting category");
    }
  };

  const handleDeletePrice = async (id) => {
    if (!window.confirm("Delete this price item?")) return;
    try {
      await deleteCostPrice(id);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Error deleting price");
    }
  };

  const shell =
    "rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900";
  const input =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  return (
    <div className="space-y-6 p-6 text-slate-900 dark:text-slate-100">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            Cost Prices
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage categories and product cost pricing
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={openAddCategory}
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiLayers />
            Add Category
          </button>

          <button
            onClick={openAddPrice}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-white transition hover:bg-blue-700"
          >
            <FiPlus />
            Add Price
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Total Products</p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {stats.totalProducts}
            </h2>
            <FiPackage className="text-xl text-blue-600" />
          </div>
        </div>

        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Active Prices</p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {stats.activePrices}
            </h2>
            <FiTrendingUp className="text-xl text-green-600" />
          </div>
        </div>

        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Avg Cost</p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              £{stats.avgCost.toFixed(2)}
            </h2>
            <FiDollarSign className="text-xl text-yellow-600" />
          </div>
        </div>

        <div className={`${shell} p-4`}>
          <p className="text-sm text-slate-500 dark:text-slate-400">Suppliers</p>
          <div className="mt-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {stats.suppliers}
            </h2>
            <FiLayers className="text-xl text-purple-600" />
          </div>
        </div>
      </div>

      <div className={`${shell} p-5`}>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Categories
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          These categories appear in the price form dropdown
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {categories.map((cat) => {
            const count = prices.filter(
              (p) => Number(p.categoryId) === Number(cat.id)
            ).length;

            return (
              <div
                key={cat.id}
                className="rounded-xl border border-slate-200 bg-white p-4 transition-colors duration-300 dark:border-slate-800 dark:bg-slate-950"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {cat.description || "No description"}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {count} items
                  </span>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => openEditCategory(cat)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(cat.id)}
                    className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}

          {categories.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400">
              No categories yet. Add your first category.
            </div>
          )}
        </div>
      </div>

      <div className={`${shell} p-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search product, supplier or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-900 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>

          <button
            onClick={() => {
              setSearch("");
              setSelectedCategory("");
              setSelectedStatus("");
            }}
            className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiFilter />
            Clear Filters
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 dark:border-slate-800 md:grid-cols-3">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={input}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={input}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="p-4 text-left">Product</th>
                <th className="p-4 text-left">Category</th>
                <th className="p-4 text-left">Supplier</th>
                <th className="p-4 text-left">Cost</th>
                <th className="p-4 text-left">Shipping</th>
                <th className="p-4 text-left">Total</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filteredPrices.map((item) => (
                <tr
                  key={item.id}
                  className="bg-white transition-colors hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                >
                  <td className="p-4 font-medium text-slate-900 dark:text-slate-100">
                    {item.productName}
                  </td>
                  <td className="p-4 text-slate-500 dark:text-slate-400">
                    {item.category?.name || "N/A"}
                  </td>
                  <td className="p-4 text-slate-500 dark:text-slate-400">
                    {item.supplier}
                  </td>
                  <td className="p-4 text-slate-900 dark:text-slate-100">
                    £{Number(item.cost || 0).toFixed(2)}
                  </td>
                  <td className="p-4 text-slate-900 dark:text-slate-100">
                    £{Number(item.shipping || 0).toFixed(2)}
                  </td>
                  <td className="p-4 font-semibold text-slate-900 dark:text-slate-100">
                    £{Number(item.total || 0).toFixed(2)}
                  </td>
                  <td className="p-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        item.status === "Active"
                          ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditPrice(item)}
                        className="rounded-lg border border-slate-300 p-2 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => handleDeletePrice(item.id)}
                        className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPrices.length === 0 && (
          <div className="py-12 text-center text-slate-500 dark:text-slate-400">
            No price items found
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
          <span>Total Items: {filteredPrices.length}</span>
          <span>Total Categories: {categories.length}</span>
        </div>
      </div>

      <AddCategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        onSave={handleSaveCategory}
        editingCategory={editingCategory}
      />

      <AddCostPriceModal
        isOpen={showPriceModal}
        onClose={() => {
          setShowPriceModal(false);
          setEditingPrice(null);
        }}
        onSave={handleSavePrice}
        editingPrice={editingPrice}
        categories={categories}
      />
    </div>
  );
}